import {
  select,
  scaleLinear,
  scaleBand,
  axisBottom,
  axisLeft,
  axisRight,
  line,
  max,
  min,
} from 'd3';
import {
  splitAndTrim,
  parseValue,
  fetchChartData,
  setupHoverHighlight,
  calculateLegendWidths,
  calculateLegendRows,
} from './chart-utils';

/**
 * Initializes dual-axis charts on the page
 */
export function initDualAxisChart() {
  const charts = document.querySelectorAll(
    '.dual-axis-chart-wrapper .chart-inner',
  );

  if (charts.length === 0) {
    return;
  }

  for (const chart of charts) {
    if (chart instanceof HTMLElement) {
      renderDualAxisChart(chart);
    }
  }
}

/**
 * Renders a single dual-axis chart
 * @param {HTMLElement} chartElement - The chart container element
 */
function renderDualAxisChart(chartElement) {
  const file = chartElement.getAttribute('data-file');
  const xColumn = chartElement.getAttribute('data-x-column') || 'Date';
  const leftLineColumn =
    chartElement.getAttribute('data-left-line-column') || '';
  const rightBarColumn =
    chartElement.getAttribute('data-right-bar-column') || '';
  const rightLineColumns =
    chartElement.getAttribute('data-right-line-columns') || '';
  const colors = chartElement.getAttribute('data-colors') || '';

  if (!file || !leftLineColumn || !rightBarColumn) {
    return;
  }

  const rightLineColumnsArray = splitAndTrim(rightLineColumns);
  const colorArray = splitAndTrim(colors);

  fetchChartData(chartElement, file, '.dual-axis-chart-wrapper', (data) => {
    createDualAxisChart(
      chartElement,
      data,
      xColumn,
      leftLineColumn,
      rightBarColumn,
      rightLineColumnsArray,
      colorArray,
    );
  });
}

/**
 * Creates a dual-axis chart with D3
 * @param {HTMLElement} container - The chart container
 * @param {Array} data - The parsed CSV data
 * @param {string} xColumn - X-axis column name
 * @param {string} leftLineColumn - Left Y-axis line column
 * @param {string} rightBarColumn - Right Y-axis bar column
 * @param {Array<string>} rightLineColumns - Right Y-axis line columns
 * @param {Array<string>} colors - Color array
 */
function createDualAxisChart(
  container,
  data,
  xColumn,
  leftLineColumn,
  rightBarColumn,
  rightLineColumns,
  colors,
) {
  // Clear container
  container.innerHTML = '';

  // Set up dimensions with space for legend
  const margin = { top: 0, right: 50, bottom: 80, left: 70 };
  const width = container.clientWidth - margin.left - margin.right;
  const height = container.clientHeight - margin.top - margin.bottom;
  const totalWidth = width + margin.left + margin.right;
  const totalHeight = height + margin.top + margin.bottom;

  // Create SVG with viewBox for responsive scaling
  const svg = select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('width', '100%')
    .style('height', 'auto')
    .style('opacity', 0)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Fade in the chart
  select(container)
    .select('svg')
    .transition()
    .duration(600)
    .style('opacity', 1);

  // Parse data
  const processedData = data.map((d) => ({
    year: parseInt(d[xColumn], 10),
    leftLine: parseValue(d[leftLineColumn]),
    rightBar: parseValue(d[rightBarColumn]),
    rightLines: rightLineColumns.map((col) => parseValue(d[col])),
  }));

  // X scale
  const xScale = scaleBand()
    .domain(processedData.map((d) => String(d.year)))
    .range([0, width])
    .padding(0.1);

  // Left Y scale (for Total Households)
  const yLeft = scaleLinear()
    .domain([0, (max(processedData, (d) => d.leftLine) || 0) * 1.1])
    .range([height, 0]);

  // Right Y scale (for percentages)
  const allRightValues = processedData.flatMap((d) => [
    d.rightBar,
    ...d.rightLines,
  ]);
  const yRight = scaleLinear()
    .domain([
      (min(allRightValues) || 0) * 1.2,
      (max(allRightValues) || 0) * 1.2,
    ])
    .range([height, 0]);

  // X axis
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(
      axisBottom(xScale)
        .tickValues(
          processedData
            .filter((d, i) => i % 2 === 0)
            .map((d) => String(d.year)),
        )
        .tickSize(0)
        .tickPadding(10),
    )
    .call((g) =>
      g
        .selectAll('text')
        .style('fill', '#1D2328')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-65)'),
    );

  // Left Y axis
  svg
    .append('g')
    .attr('class', 'y-axis-left')
    .call(
      axisLeft(yLeft)
        .tickFormat((d) => d.toLocaleString())
        .tickSize(-width)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').remove())
    .call((g) => g.selectAll('.tick line').attr('stroke', '#e0e0e0'))
    .call((g) =>
      g.selectAll('text').style('fill', '#999').style('font-size', '14px'),
    );

  // Right Y axis
  svg
    .append('g')
    .attr('class', 'y-axis-right')
    .attr('transform', `translate(${width},0)`)
    .call(
      axisRight(yRight)
        .tickFormat((d) => `${d}%`)
        .tickSize(0)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').attr('stroke', 'transparent'))
    .call((g) =>
      g.selectAll('text').style('fill', '#999').style('font-size', '14px'),
    );

  // Bars (right axis)
  const bars = svg
    .selectAll('.bar')
    .data(processedData)
    .enter()
    .append('rect')
    .attr('class', 'bar')
    .attr('x', (d) => xScale(String(d.year)) || 0)
    .attr('y', (d) => (d.rightBar >= 0 ? yRight(d.rightBar) : yRight(0)))
    .attr('width', xScale.bandwidth())
    .attr('height', (d) =>
      d.rightBar >= 0
        ? yRight(0) - yRight(d.rightBar)
        : yRight(d.rightBar) - yRight(0),
    )
    .attr('fill', colors[0] || '#264C68')
    .attr('data-series', rightBarColumn)
    .style('cursor', 'pointer');

  // Left line (Total Households)
  const leftLinePath = line()
    .x((d) => (xScale(String(d.year)) || 0) + xScale.bandwidth() / 2)
    .y((d) => yLeft(d.leftLine));

  const leftLine = svg
    .append('path')
    .datum(processedData)
    .attr('fill', 'none')
    .attr('stroke', colors[1] || '#4EE5CC')
    .attr('stroke-width', 2)
    .attr('d', (d) => leftLinePath(d))
    .attr('data-series', leftLineColumn)
    .style('cursor', 'pointer');

  // Right lines (percentages)
  const rightLines = [];
  for (let idx = 0; idx < rightLineColumns.length; idx++) {
    const col = rightLineColumns[idx];
    const rightLinePath = line()
      .x((d) => (xScale(String(d.year)) || 0) + xScale.bandwidth() / 2)
      .y((d) => yRight(d.rightLines[idx]));

    const path = svg
      .append('path')
      .datum(processedData)
      .attr('fill', 'none')
      .attr('stroke', colors[2 + idx] || '#999999')
      .attr('stroke-width', 2)
      .attr('d', (d) => rightLinePath(d))
      .attr('data-series', col)
      .style('cursor', 'pointer');

    rightLines.push(path);
  }

  // Create legend
  const legendItems = [
    { label: rightBarColumn, color: colors[0] || '#264C68', type: 'rect' },
    { label: leftLineColumn, color: colors[1] || '#4EE5CC', type: 'line' },
    ...rightLineColumns.map((col, idx) => ({
      label: col,
      color: colors[2 + idx] || '#999999',
      type: 'line',
    })),
  ];

  // Calculate legend widths and rows for wrapping
  const legendWidths = calculateLegendWidths(svg, legendItems);
  const rows = calculateLegendRows(legendWidths, width);

  const rowHeight = 20;
  const legendHeight = rows.length * rowHeight;

  // Update SVG height to accommodate legend
  const newTotalHeight = height + margin.top + margin.bottom + legendHeight;
  select(container)
    .select('svg')
    .attr('viewBox', `0 0 ${totalWidth} ${newTotalHeight}`);

  // Create actual legend with multiple rows
  const legend = svg
    .append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(0,${height + 60})`);

  const legendElements = [];

  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    const row = rows[rowIdx];
    const rowWidth = row.reduce((sum, i) => sum + legendWidths[i], 0);
    const rowX = (width - rowWidth) / 2;

    let currentX = 0;

    for (const i of row) {
      const item = legendItems[i];
      const legendItem = legend
        .append('g')
        .attr('class', 'legend-item')
        .attr('data-series', item.label)
        .attr(
          'transform',
          `translate(${rowX + currentX},${rowIdx * rowHeight})`,
        )
        .style('cursor', 'pointer');

      if (item.type === 'rect') {
        legendItem
          .append('rect')
          .attr('class', 'legend-shape')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', item.color);
      } else {
        legendItem
          .append('line')
          .attr('class', 'legend-shape')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr('x2', 12)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2);
      }

      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '11px')
        .style('fill', '#444')
        .style(
          'font-family',
          '"Roboto", "Helvetica Neue", "HelveticaNeue", "Helvetica", "Arial", sans-serif',
        )
        .text(item.label);

      legendElements.push({ label: item.label, element: legendItem });
      currentX += legendWidths[i];
    }
  }

  // Collect all chart elements for hover highlighting
  const chartElements = [
    { series: rightBarColumn, element: bars },
    { series: leftLineColumn, element: leftLine },
    ...rightLineColumns.map((col, idx) => ({
      series: col,
      element: rightLines[idx],
    })),
  ];

  // Setup hover highlighting
  setupHoverHighlight(svg, legendElements, chartElements);
}
